// src/features/crypto-ranking/components/UniverseTable.tsx
//
// Data-dense, client-sortable leaderboard for the FULL crypto universe
// (the ~18-50 liquidity-screened coins). Sibling of
// features/commodities-ranking/components/UniverseTable. It reuses the
// shared heat-coloured z-score badge (<ScoreBadge>) but uses the crypto
// contract: the three factor columns are `momentum_z_score`,
// `volatility_z_score` (INVERTED — a higher score means lower realised
// vol) and `liquidity_z_score`, the composite is `forge_ultra_signal`,
// and `annualized_volatility` is the display-only risk metric. It adds:
//   - an Asset column (the coin base, derived client-side from the USD
//     pair symbol — the backend universe payload carries no `name`),
//   - click-to-sort column headers with a direction caret,
//   - a 90-day Forge Ultra Signal sparkline column (data is fetched in one
//     batched request by the parent and passed down as a map),
//   - row-hover callback so the parent can cross-highlight the matching
//     dot on the Risk vs Return scatter.
//
// Crypto has no per-symbol detail endpoint (no COT / quick-stats), so —
// unlike the commodities table — the ticker is NOT a click target.
// Sorting happens entirely in the parent (it owns the already-sorted
// array); this component is purely presentational.

import { ScoreBadge } from '../../../shared/ui/RankingTable';
import { baseAsset, type HistoryPoint, type UniverseRow } from '../api';
import { Sparkline } from './Sparkline';

// Columns the user can sort by. ``label`` is the header text; ``align``
// drives the cell alignment so numeric columns stay right/centre.
export type SortKey =
    | 'universe_rank'
    | 'symbol'
    | 'forge_ultra_signal'
    | 'momentum_z_score'
    | 'volatility_z_score'
    | 'liquidity_z_score'
    | 'annualized_volatility';

interface UniverseTableProps {
    data: UniverseRow[];
    sparklines?: Record<string, HistoryPoint[]>;
    sortKey: SortKey;
    sortDesc: boolean;
    onSort: (key: SortKey) => void;
    onRowHover?: (symbol: string | null) => void;
    /** Symbols for which a sparkline was requested (so we can show a
     *  subtle "loading" placeholder for rows still awaiting data). */
    sparklineSymbols?: Set<string>;
    /** When provided, the ticker becomes a click target that opens the
     *  per-coin detail pop-over. */
    onSymbolClick?: (symbol: string) => void;
}

interface ColumnDef {
    key: SortKey;
    label: string;
    align: 'left' | 'center' | 'right';
    sortable: boolean;
}

const COLUMNS: ColumnDef[] = [
    { key: 'universe_rank', label: 'Rank', align: 'left', sortable: true },
    { key: 'symbol', label: 'Symbol', align: 'left', sortable: true },
    {
        key: 'forge_ultra_signal',
        label: 'Forge Signal',
        align: 'right',
        sortable: true,
    },
    {
        key: 'momentum_z_score',
        label: 'Momentum (z)',
        align: 'center',
        sortable: true,
    },
    {
        key: 'volatility_z_score',
        label: 'Volatility (z)',
        align: 'center',
        sortable: true,
    },
    {
        key: 'liquidity_z_score',
        label: 'Liquidity (z)',
        align: 'center',
        sortable: true,
    },
    {
        key: 'annualized_volatility',
        label: 'Vol (Ann.)',
        align: 'right',
        sortable: true,
    },
];

const alignClass = (align: ColumnDef['align']): string =>
    align === 'right'
        ? 'text-right'
        : align === 'center'
          ? 'text-center'
          : 'text-left';

export const UniverseTable = ({
    data,
    sparklines,
    sortKey,
    sortDesc,
    onSort,
    onRowHover,
    sparklineSymbols,
    onSymbolClick,
}: UniverseTableProps) => {
    return (
        <div className="overflow-x-auto rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900">
            <table className="w-full text-sm text-left">
                <thead className="text-xs uppercase bg-slate-50 dark:bg-slate-950/50 text-slate-500 dark:text-slate-400 border-b border-slate-200 dark:border-slate-800">
                    <tr>
                        {COLUMNS.map((col) => {
                            const isActive = sortKey === col.key;
                            return (
                                <th
                                    key={col.key}
                                    scope="col"
                                    className={`px-6 py-4 font-semibold ${alignClass(col.align)} ${
                                        col.sortable
                                            ? 'cursor-pointer select-none hover:text-slate-700 dark:hover:text-slate-200'
                                            : ''
                                    }`}
                                    onClick={
                                        col.sortable
                                            ? () => onSort(col.key)
                                            : undefined
                                    }
                                    aria-sort={
                                        isActive
                                            ? sortDesc
                                                ? 'descending'
                                                : 'ascending'
                                            : undefined
                                    }
                                >
                                    {col.label}
                                    {isActive && (
                                        <span className="ml-1 text-green-600 dark:text-emerald-400">
                                            {sortDesc ? '▼' : '▲'}
                                        </span>
                                    )}
                                </th>
                            );
                        })}
                        <th
                            scope="col"
                            className="px-6 py-4 font-semibold text-center"
                        >
                            90D Trend
                        </th>
                    </tr>
                </thead>
                <tbody className="divide-y divide-slate-200 dark:divide-slate-800">
                    {data.length === 0 ? (
                        <tr>
                            <td
                                colSpan={COLUMNS.length + 1}
                                className="px-6 py-16 text-center text-slate-500 dark:text-slate-400"
                            >
                                No ranked coins available yet.
                            </td>
                        </tr>
                    ) : (
                        data.map((row) => {
                            const series = sparklines?.[row.symbol];
                            const awaiting =
                                sparklineSymbols?.has(row.symbol) &&
                                series === undefined;
                            return (
                                <tr
                                    key={row.symbol}
                                    className="hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors"
                                    onMouseEnter={() => onRowHover?.(row.symbol)}
                                    onMouseLeave={() => onRowHover?.(null)}
                                >
                                    <td className="px-6 py-4 whitespace-nowrap font-bold text-slate-900 dark:text-white">
                                        #{row.universe_rank}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        {onSymbolClick ? (
                                            <button
                                                type="button"
                                                className="font-bold text-green-600 dark:text-emerald-400 hover:underline focus:outline-none focus:underline cursor-pointer"
                                                onClick={() =>
                                                    onSymbolClick(row.symbol)
                                                }
                                            >
                                                {baseAsset(row.symbol)}
                                            </button>
                                        ) : (
                                            <span className="font-bold text-green-600 dark:text-emerald-400">
                                                {baseAsset(row.symbol)}
                                            </span>
                                        )}
                                        <span className="ml-2 text-xs font-mono text-slate-400 dark:text-slate-500">
                                            {row.symbol}
                                        </span>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-right font-bold text-slate-900 dark:text-white">
                                        {row.forge_ultra_signal.toFixed(2)}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-center">
                                        <ScoreBadge score={row.momentum_z_score} />
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-center">
                                        <ScoreBadge score={row.volatility_z_score} />
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-center">
                                        <ScoreBadge score={row.liquidity_z_score} />
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-right text-slate-500 dark:text-slate-400">
                                        {(row.annualized_volatility * 100).toFixed(1)}%
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-center">
                                        {awaiting ? (
                                            <span className="inline-block h-7 w-24 rounded bg-slate-100 dark:bg-slate-800 animate-pulse" />
                                        ) : (
                                            <Sparkline series={series ?? []} />
                                        )}
                                    </td>
                                </tr>
                            );
                        })
                    )}
                </tbody>
            </table>
        </div>
    );
};

export default UniverseTable;
